import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronLeft, FiHelpCircle } from 'react-icons/fi';
import { t } from 'i18next';
import { useFormik } from 'formik';
import { useQuery } from 'jsonapi-react';
import moment from 'moment';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Loader } from '@/components/custom/loader';
import { TableGRE } from './TableGRE';
import useReportAbilities from '@/hooks/useReportAbilities';
import usePermission from '@/hooks/usePermission';
import toast from 'react-hot-toast';
import { Modal as SimpleModal } from '@/components/custom/modal/modal';
import { LineChartReport } from './LineChart';
import { EmptyState } from '@/components/custom/empty-state';

export function StudyHours() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const session = JSON.parse(localStorage.getItem('session'));

  const filters = useFormik({
    initialValues: {
      interval: 'day',
      'start-date': moment().subtract(30, 'days').format('DD/MM/YYYY'),
      'end-date': moment().format('DD/MM/YYYY')
    }
  });

  const today = moment().format(t('datesForm.formatDate'));
  const { reportAbilities, getReportAbilities } = useReportAbilities();
  const [showModal, setShowModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDateError, setIsDateError] = useState(false);

  let filter = {};

  if (filters.values.interval) {
    filter.time_interval = filters.values.interval;
  }

  if (filters.values['start-date'] && filters.values['end-date']) {
    filter.initial_date = filters.values['start-date'];
    filter.end_date = filters.values['end-date'];
  }

  const {
    data: report,
    isLoading,
    error
  } = useQuery([
    'reports/vimeo_analytics_hours',
    {
      ...filter
    }
  ]);

  useEffect(() => {
    if (error) {
      if (error?.message === 'Parâmetros de data inválidos') {
        toast.error('O intervalo entre a data inicial e final deve ser de no máximo 30 dias');
        setItems([]);
        setIsDateError(true);
      } else {
        toast.error(error?.message || 'Erro ao buscar dados do relatório');
      }
    } else {
      setIsDateError(false);
    }
  }, [error]);

  const startDate = filters.values['start-date'] ? moment(filters.values['start-date'], 'DD/MM/YYYY').format('DD/MM/YYYY') : null;
  const endDate = filters.values['end-date'] ? moment(filters.values['end-date'], 'DD/MM/YYYY').format('DD/MM/YYYY') : null;

  const kpis = [
    {
      id: 1,
      title: startDate !== 'Invalid date' && startDate !== null && endDate !== 'Invalid date' && endDate !== null ? `Total de visualizações de ${startDate} a ${endDate}` : 'Total de visualizações',
      value: report?.kpis?.total_views || '-',
      variation: 'report-kpi-card--users'
    },
    {
      id: 2,
      title: startDate !== 'Invalid date' && startDate !== null && endDate !== 'Invalid date' && endDate !== null ? `Tempo total assistido de ${startDate} a ${endDate}` : 'Tempo total assistido',
      value: report?.kpis?.total_watched_hours ? `${report?.kpis?.total_watched_hours} hora${report?.kpis?.total_watched_hours > 1 ? 's' : ''}` : '-',
      variation: 'report-kpi-card--clock'
    }
  ];

  useEffect(() => {
    getReportAbilities();
  }, []);

  useEffect(() => {
    if (report?.items) {
      const parsedReports = report?.items?.map((item, index) => ({ ...item, position: index + 1 }));
      setItems(parsedReports);
    }
  }, [report]);

  const getFilteredChartData = () => {
    if (!items || items.length === 0) return [];

    return [...items]
      .sort((a, b) => moment(a.period_start).valueOf() - moment(b.period_start).valueOf())
      .map(item => ({
        time: moment(item.period_start).format('DD/MM'),
        interface: parseFloat((item.total_watched_hours || 0).toFixed(2))
      }));
  };

  const handleDownload = () => {
    setIsDownloading(true);
    const params = new URLSearchParams();

    if (filters.values.interval) {
      params.append('time_interval', filters.values.interval);
    }

    const startDateMoment = moment(filters.values['start-date'], 'DD/MM/YYYY');
    const endDateMoment = moment(filters.values['end-date'], 'DD/MM/YYYY');

    if (startDateMoment.isValid()) {
      params.append('initial_date', startDateMoment.format('YYYY-MM-DD'));
    }
    if (endDateMoment.isValid()) {
      params.append('end_date', endDateMoment.format('YYYY-MM-DD'));
    }
    fetch(`${process.env.REACT_APP_DEV_URL}/reports/vimeo_analytics_hours.xlsx?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${session?.user?.authToken}`
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Erro ao baixar o relatório');
        }
        return response.blob();
      })
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `relatorio_cadastro_e_ativos_${moment().format('YYYYMMDDHHmmss')}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        toast.success('Relatório baixado com sucesso!');
      })
      .catch(error => {
        console.error('Erro no download:', error);
        toast.error('Erro ao baixar o relatório');
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  return (
    <div className="report">
      <header className="report__header">
        <h1 className="report__title">
          <button
            className="card__icon-wrapper"
            aria-label="Voltar"
            title="Voltar"
            onClick={() => navigate(-1)}
          >
            <FiChevronLeft className="card__icon" />
          </button>
          Horas de Estudo
        </h1>
        <p className="report__description">Apresenta as horas de estudo dos alunos com base em dados integrados do Vimeo, incluindo total de visualizações e tempo total assistido.</p>
      </header>

      <div className="report__body">
        <div className="flex flex-col gap-2">
          <div className="report__filter">
            <div className="report-form__datepicker">
              <input
                className="report-form__control"
                id="start-date"
                name="start-date"
                type="date"
                value={moment(filters.values['start-date'], 'DD/MM/YYYY').format('YYYY-MM-DD')}
                onChange={e => filters.setFieldValue('start-date', moment(e.target.value, 'YYYY-MM-DD').format('DD/MM/YYYY'))}
                title="Data de início"
                aria-label="Data de início"
              />
              <input
                className="report-form__control"
                id="end-date"
                name="end-date"
                type="date"
                min={today}
                value={moment(filters.values['end-date'], 'DD/MM/YYYY').format('YYYY-MM-DD')}
                onChange={e => filters.setFieldValue('end-date', moment(e.target.value, 'YYYY-MM-DD').format('DD/MM/YYYY'))}
                title="Data de fim"
                aria-label="Data de fim"
              />
            </div>
          </div>

          {isDateError && <p className="mt-2 text-sm font-bold italic text-danger">O intervalo entre a data inicial e final deve ser de no máximo 30 dias</p>}
        </div>

        {isLoading && <Loader />}

        {!isLoading && items && items.length > 0 && (
          <>
            <div className="report-widget">
              <div className="report-widget__header">
                <h3 className="report-widget__title">Visão geral</h3>
              </div>

              <div className="report-widget__body">
                <div className="report-kpi-card-wrapper">
                  {kpis.map(kpi => (
                    <div
                      className={`report-kpi-card ${kpi.variation}`}
                      key={kpi.id}
                    >
                      {kpi.help && (
                        <>
                          <FiHelpCircle
                            className="report-kpi-card__help-icon"
                            data-tooltip-id={kpi.id}
                            data-tooltip-content={kpi.help}
                          />
                          <ReactTooltip
                            id={kpi.id.toString()}
                            className="tooltip"
                          />
                        </>
                      )}
                      <h3 className="report-kpi-card__title">{kpi.title}</h3>
                      <p
                        className="report-kpi-card__value"
                        title={kpi.value}
                      >
                        {kpi.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {reportAbilities?.['horas-estudo'] && (
              <>
                <div className="report-widget">
                  <div className="report-widget__header">
                    <h3 className="report-widget__title">{startDate !== 'Invalid date' && startDate !== null && endDate !== 'Invalid date' && endDate !== null ? `Horas assistidas de ${startDate} a ${endDate}` : 'Horas assistidas'}</h3>

                    <ReactTooltip
                      id="access"
                      className="tooltip"
                    />
                  </div>

                  <div className="report-widget__body">
                    <LineChartReport report={getFilteredChartData()} />
                  </div>
                </div>

                <div className="report-widget">
                  <div className="report-widget__header">
                    <h3 className="report-widget__title">
                      {startDate !== 'Invalid date' && startDate !== null && endDate !== 'Invalid date' && endDate !== null ? `Diário de horas assistidas de ${startDate} a ${endDate}` : 'Diário de horas assistidas'}
                    </h3>
                    <div
                      className="report-widget__header"
                      style={{ justifyContent: 'flex-end', marginBottom: '24px' }}
                    >
                      <div className="report-widget__filter">
                        {usePermission('Download de relatórios', 'Relatórios') && (
                          <button
                            className={`btn btn--outline ${isDownloading ? 'disabled' : ''}`}
                            type="button"
                            disabled={isDownloading}
                            onClick={() => setShowModal(true)}
                          >
                            {isDownloading ? 'Baixando...' : 'Exportar relatório'}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className={'filter-bar filter-bar--borderless mb-0'}>
                      <div className="filter-bar__inner">
                        <input
                          className="report-form__control"
                          aria-label={t('textsCommon.research')}
                          placeholder={t('textsCommon.research')}
                          type="search"
                          name="search"
                          id="search"
                          onChange={e => filters.setFieldValue('searchTerm', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="report-widget__body">
                    <div className="round-dg-wrapper">
                      <TableGRE
                        dates={items}
                        searchValue={filters.values.searchTerm}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {!isLoading && (!items || items.length === 0) && (
          <div className="flex flex-col items-center py-28">
            <EmptyState
              variant="plain"
              type="data"
              text="Ainda não temos dados para mostrar"
            />
          </div>
        )}
      </div>

      <SimpleModal
        show={showModal}
        onClose={() => setShowModal(false)}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}
        >
          <h3 className="modal__dialog-title">Atenção!</h3>
          <div style={{ textAlign: 'center' }}>
            <p className="u-text-neutral-dark">
              O relatório será exportado em <strong>Excel</strong> e contém <strong>dados sensíveis</strong>. <br /> O uso e o compartilhamento dessas informações são de sua inteira responsabilidade, devendo estar em conformidade com as normas de
              segurança e privacidade.
            </p>

            <p className="u-text-neutral-dark">Clique em "Confirmar" para prosseguir com o download.</p>

            <div className="form__col">
              <button
                onClick={() => {
                  handleDownload();
                  setShowModal(false);
                }}
                className={`btn btn--primary btn--full ${isDownloading ? 'disabled' : ''}`}
                type="button"
                disabled={isDownloading}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      </SimpleModal>
    </div>
  );
}
